import { useMemo } from "react";
import { useQuery } from "@powersync/react";
import { useApp } from "@/app/AppContext";

// Client portfolio + Paper Trail read models. Clients are client_contacts; a
// client's scope is every location they're linked to plus that location's
// descendants (mobile_zone_memberships), so a farm owner sees their fields.

export interface ClientLoc { id: string; label: string | null; level: string | null; latitude: string | null; longitude: string | null; geometry: string | null; description: string | null; direct: boolean; parentId: string | null }
export interface ClientRow {
  id: string; name: string | null; email: string | null; phone: string | null; role: string | null; notes: string | null; created_at: string | null;
  locations: ClientLoc[]; scope: string[]; lastVisit: string | null; visits30: number; openTasks: number;
}

interface Link { client_contact_id: string; map_feature_id: string }
interface Loc { id: string; label: string | null; level: string | null; latitude: string | null; longitude: string | null; geometry: string | null; description: string | null }
interface Zone { zone_id: string; member_id: string }

export function useClientRows() {
  const { teamId } = useApp();
  const clients = useQuery<Omit<ClientRow, "locations" | "scope" | "lastVisit" | "visits30" | "openTasks">>("SELECT id, name, email, phone, role, notes, created_at FROM client_contacts WHERE team_id = ? ORDER BY name COLLATE NOCASE", [teamId]);
  const links = useQuery<Link>("SELECT client_contact_id, map_feature_id FROM mobile_feature_contacts");
  const locs = useQuery<Loc>("SELECT mf.id, mf.label, mf.level, mf.latitude, mf.longitude, mf.geometry, mf.description FROM map_features mf JOIN maps m ON m.id = mf.map_id WHERE m.team_id = ?", [teamId]);
  const zones = useQuery<Zone>("SELECT zone_id, member_id FROM mobile_zone_memberships");
  const visits = useQuery<{ map_feature_id: string; at: string | null }>("SELECT map_feature_id, coalesce(started_at, created_at) AS at FROM mobile_field_notes");
  const tasks = useQuery<{ map_feature_id: string }>("SELECT map_feature_id FROM mobile_reminders WHERE completed_at IS NULL AND canceled_at IS NULL AND map_feature_id IS NOT NULL");

  const data = useMemo<ClientRow[]>(() => {
    const byId = new Map(locs.data.map((l) => [l.id, l]));
    const kids = new Map<string, string[]>();
    for (const z of zones.data) kids.set(z.zone_id, [...(kids.get(z.zone_id) || []), z.member_id]);
    const since = Date.now() - 30 * 86_400_000;
    return clients.data.map((c) => {
      const direct = links.data.filter((l) => l.client_contact_id === c.id).map((l) => l.map_feature_id).filter((id) => byId.has(id));
      const scope = new Set<string>();
      const parentOf = new Map<string, string | null>();
      const walk = (id: string, parent: string | null, depth = 0) => {
        if (scope.has(id) || depth > 6) return;
        scope.add(id); parentOf.set(id, parent);
        (kids.get(id) || []).forEach((k) => walk(k, id, depth + 1));
      };
      direct.forEach((id) => walk(id, null));
      const ids = [...scope];
      const vs = visits.data.filter((v) => scope.has(v.map_feature_id));
      const last = vs.reduce<string | null>((a, v) => (v.at && (!a || v.at > a) ? v.at : a), null);
      return {
        ...c,
        locations: ids.map((id) => ({ ...byId.get(id)!, direct: direct.includes(id), parentId: parentOf.get(id) ?? null })),
        scope: ids,
        lastVisit: last,
        visits30: vs.filter((v) => v.at && new Date(v.at).getTime() >= since).length,
        openTasks: tasks.data.filter((t) => scope.has(t.map_feature_id)).length,
      };
    });
  }, [clients.data, links.data, locs.data, zones.data, visits.data, tasks.data]);

  // Portfolio totals over the union of client scopes (shared farms count once).
  const totals = useMemo(() => {
    const union = new Set(data.flatMap((c) => c.scope));
    const since = Date.now() - 30 * 86_400_000;
    return {
      locations: union.size,
      visits30: visits.data.filter((v) => union.has(v.map_feature_id) && v.at && new Date(v.at).getTime() >= since).length,
      openTasks: tasks.data.filter((t) => union.has(t.map_feature_id)).length,
    };
  }, [data, visits.data, tasks.data]);

  return { data, totals, isLoading: clients.isLoading };
}

export type FileKind = "pdf" | "image" | "sheet" | "doc" | "report" | "other";
export interface TrailRow {
  id: string; kind: "file" | "report"; name: string; type: FileKind; contentType: string | null; size: number | null;
  locationId: string | null; locationLabel: string | null; locationLevel: string | null; visitId: string | null; visitTitle: string | null;
  date: string | null; storageKey: string | null; reportId: string | null; approver: string | null;
}

export function fileKind(contentType?: string | null, name?: string | null): FileKind {
  const t = (contentType || "").toLowerCase();
  const n = (name || "").toLowerCase();
  if (t.includes("pdf") || n.endsWith(".pdf")) return "pdf";
  if (t.startsWith("image/")) return "image";
  if (t.includes("sheet") || t.includes("excel") || t.includes("csv") || /\.(xlsx?|csv)$/.test(n)) return "sheet";
  if (t.includes("word") || t.includes("document") || t.startsWith("text/") || /\.(docx?|txt|rtf)$/.test(n)) return "doc";
  return "other";
}

// Every document the team holds, linked to where it came from: visit
// attachments, farm-report files, and signed visit reports.
export function usePaperTrail() {
  const { teamId } = useApp();
  const files = useQuery<{ id: string; filename: string | null; attachment_name: string | null; content_type: string | null; byte_size: number | null; field_note_id: string | null; map_feature_id: string | null; storage_key: string | null; created_at: string | null; label: string | null; level: string | null; visit_title: string | null }>(
    `SELECT a.id, a.filename, a.attachment_name, a.content_type, a.byte_size, a.field_note_id, a.map_feature_id, a.storage_key, a.created_at, mf.label, mf.level, fn.title AS visit_title
     FROM mobile_field_note_attachments a JOIN map_features mf ON mf.id = a.map_feature_id JOIN maps m ON m.id = mf.map_id
     LEFT JOIN mobile_field_notes fn ON fn.id = a.field_note_id WHERE m.team_id = ?`, [teamId]);
  const farm = useQuery<{ id: string; filename: string | null; content_type: string | null; byte_size: number | null; map_feature_id: string | null; storage_key: string | null; created_at: string | null; label: string | null; level: string | null }>(
    `SELECT d.id, d.filename, d.content_type, d.byte_size, d.map_feature_id, d.storage_key, d.created_at, mf.label, mf.level
     FROM mobile_farm_report_downloads d JOIN map_features mf ON mf.id = d.map_feature_id JOIN maps m ON m.id = mf.map_id WHERE m.team_id = ?`, [teamId]);
  const reports = useQuery<{ id: string; field_note_id: string | null; map_feature_id: string | null; config_name: string | null; approved_at: string | null; approver_name: string | null; label: string | null; level: string | null; visit_title: string | null; started_at: string | null }>(
    `SELECT r.id, r.field_note_id, r.map_feature_id, r.config_name, r.approved_at, r.approver_name, mf.label, mf.level, fn.title AS visit_title, fn.started_at
     FROM mobile_custom_report_statuses r JOIN map_features mf ON mf.id = r.map_feature_id JOIN maps m ON m.id = mf.map_id
     LEFT JOIN mobile_field_notes fn ON fn.id = r.field_note_id WHERE m.team_id = ? AND r.approved_at IS NOT NULL`, [teamId]);

  const data = useMemo<TrailRow[]>(() => {
    const rows: TrailRow[] = [
      ...files.data.map((f) => {
        const name = f.attachment_name || f.filename || "Attachment";
        return { id: `a:${f.id}`, kind: "file" as const, name, type: fileKind(f.content_type, name), contentType: f.content_type, size: f.byte_size, locationId: f.map_feature_id, locationLabel: f.label, locationLevel: f.level, visitId: f.field_note_id, visitTitle: f.visit_title, date: f.created_at, storageKey: f.storage_key, reportId: null, approver: null };
      }),
      ...farm.data.map((f) => ({ id: `f:${f.id}`, kind: "file" as const, name: f.filename || "Farm report", type: fileKind(f.content_type, f.filename), contentType: f.content_type, size: f.byte_size, locationId: f.map_feature_id, locationLabel: f.label, locationLevel: f.level, visitId: null, visitTitle: null, date: f.created_at, storageKey: f.storage_key, reportId: null, approver: null })),
      ...reports.data.map((r) => ({
        id: `r:${r.id}`, kind: "report" as const, type: "report" as const, contentType: "application/pdf", size: null,
        name: `${r.label ?? "Visit"} — ${r.started_at ? new Date(r.started_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""} ${r.config_name ?? "report"}`.trim(),
        locationId: r.map_feature_id, locationLabel: r.label, locationLevel: r.level, visitId: r.field_note_id, visitTitle: r.visit_title, date: r.approved_at, storageKey: null, reportId: r.id, approver: r.approver_name,
      })),
    ];
    return rows.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [files.data, farm.data, reports.data]);

  return { data, isLoading: files.isLoading && reports.isLoading };
}

export function formatBytes(n?: number | null) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 ** 2).toFixed(1)} MB`;
}
